<script setup lang="ts">
import { Loader2, ShieldCheck } from '@lucide/vue'
import { computed, defineAsyncComponent, ref } from 'vue'

import { Button } from '@/components/ui/button'
import { apiFetch, useApiMutation } from '@ja/portal-query'

import { useCommunicationTranslations } from '../../composables/useCommunicationTranslations'

const VueSignaturePad = defineAsyncComponent(() =>
  import('@selemondev/vue3-signature-pad').then(m => m.VueSignaturePad),
)

const props = withDefaults(
  defineProps<{
    communicationId: string
    requiresSignature: boolean
    apiEndpoint?: string
    httpMethod?: string
    consentText?: string
    /** When false and requiresSignature, blocks the form until all files are reviewed. */
    allFilesViewed?: boolean
  }>(),
  {
    apiEndpoint: undefined,
    httpMethod: 'PATCH',
    consentText: undefined,
    allFilesViewed: true,
  },
)

const emit = defineEmits<{
  acknowledged: []
}>()

const { tm } = useCommunicationTranslations('communication')

// --- Acknowledgment state ---
const acknowledgmentCheckbox = ref(false)
const signatureRef = ref<InstanceType<typeof VueSignaturePad> | null>(null)
const hasSignatureStrokes = ref(false)

const resolvedEndpoint = computed(
  () => props.apiEndpoint ?? `/api/communications/${props.communicationId}/acknowledge`,
)

const signerTimezone = computed(() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  }
  catch {
    return null
  }
})

const filesBlocked = computed(() =>
  props.requiresSignature && !props.allFilesViewed,
)

const canSubmit = computed(() => {
  if (filesBlocked.value) return false
  if (!acknowledgmentCheckbox.value) return false
  if (props.requiresSignature && !hasSignatureStrokes.value) return false
  return true
})

const onSignatureEnd = () => {
  if (signatureRef.value) {
    hasSignatureStrokes.value = !signatureRef.value.isCanvasEmpty()
  }
}

const clearSignature = () => {
  signatureRef.value?.clearCanvas()
  hasSignatureStrokes.value = false
}

const undoSignature = () => {
  signatureRef.value?.undo()
  if (signatureRef.value) {
    hasSignatureStrokes.value = !signatureRef.value.isCanvasEmpty()
  }
}

interface CommunicationAckPayload {
  checkbox_value: boolean
  signature?: string
  comment?: string
  signer_timezone?: string | null
  privacy_consent_at?: string | null
  consent_text?: string
}

const { mutate: acknowledgeMutation, isPending: isAcknowledging } = useApiMutation({
  mutationFn: (payload: CommunicationAckPayload) =>
    apiFetch(resolvedEndpoint.value, {
      method: props.httpMethod,
      body: JSON.stringify(payload),
    }),
  successMessage: tm('detail.communication_acknowledged'),
  onSuccess: () => {
    acknowledgmentCheckbox.value = false
    hasSignatureStrokes.value = false
    signatureRef.value?.clearCanvas()
    emit('acknowledged')
  },
})

const handleSubmit = () => {
  if (!canSubmit.value || isAcknowledging.value) return

  const payload: CommunicationAckPayload = {
    checkbox_value: acknowledgmentCheckbox.value,
  }

  if (props.requiresSignature && signatureRef.value) {
    const svgString = signatureRef.value.saveSignature('image/svg+xml')
    if (svgString) {
      payload.signature = svgString
    }
  }

  // Legal envelope fields
  if (signerTimezone.value) {
    payload.signer_timezone = signerTimezone.value
  }

  // Privacy consent is recorded automatically when the user submits
  if (props.requiresSignature) {
    payload.privacy_consent_at = new Date().toISOString()
    if (props.consentText) {
      payload.consent_text = props.consentText
    }
  }

  acknowledgeMutation(payload)
}
</script>

<template>
  <div class="rounded-xl border border-amber-200 bg-amber-50/50 p-5 dark:border-amber-800/50 dark:bg-amber-950/30">
    <h3
      v-if="requiresSignature"
      class="text-sm font-semibold text-amber-800 dark:text-amber-200"
    >
      {{ tm('detail.signature_required') }}
    </h3>
    <h3 v-else class="sr-only">
      {{ tm('detail.acknowledgment_required') }}
    </h3>

    <div class="space-y-4" :class="requiresSignature ? 'mt-4' : ''">
      <!-- Files not reviewed notice -->
      <div
        v-if="filesBlocked"
        class="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-100/60 px-3 py-2.5 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
      >
        <ShieldCheck class="size-4 shrink-0" />
        {{ tm('detail.review_all_files_notice') }}
      </div>

      <!-- Checkbox -->
      <div>
        <label
          class="flex items-start gap-3 group"
          :class="filesBlocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'"
        >
          <input
            v-model="acknowledgmentCheckbox"
            type="checkbox"
            :disabled="filesBlocked"
            class="mt-0.5 size-4 rounded border-amber-300 text-primary focus:ring-primary dark:border-amber-600 disabled:opacity-50"
          />
          <span class="text-sm text-amber-800 transition-colors group-hover:text-amber-900 dark:text-amber-200 dark:group-hover:text-amber-100">
            {{ consentText ?? tm('detail.acknowledgment_checkbox') }}
          </span>
        </label>
        <!-- Privacy caption (signature-required only) -->
        <p
          v-if="requiresSignature"
          class="ml-7 mt-1.5 text-xs leading-relaxed text-amber-600/80 dark:text-amber-400/70"
        >
          <ShieldCheck class="mr-1 inline size-3 align-text-top" />
          {{ tm('detail.privacy_caption') }}
        </p>
      </div>

      <!-- Signature Pad + Comment (revealed after checking the box) -->
      <Transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 -translate-y-2 max-h-0"
        enter-to-class="opacity-100 translate-y-0 max-h-[500px]"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100 translate-y-0 max-h-[500px]"
        leave-to-class="opacity-0 -translate-y-2 max-h-0"
      >
        <div v-if="acknowledgmentCheckbox" class="space-y-4 overflow-hidden">
          <!-- Signature Pad -->
          <div v-if="requiresSignature" class="space-y-2">
            <label class="block text-sm font-medium text-amber-800 dark:text-amber-200">
              {{ tm('detail.sign_here') }}
            </label>
            <div class="overflow-hidden rounded-lg border-2 border-dashed border-amber-300 bg-white dark:border-amber-700 dark:bg-gray-900">
              <VueSignaturePad
                ref="signatureRef"
                width="100%"
                height="200px"
                :options="{ penColor: 'rgb(0, 0, 0)', backgroundColor: 'rgb(255, 255, 255)' }"
                @end-stroke="onSignatureEnd"
              />
            </div>
            <div class="flex gap-2">
              <Button variant="outline" size="sm" @click="clearSignature">
                {{ tm('detail.clear_signature') }}
              </Button>
              <Button variant="outline" size="sm" @click="undoSignature">
                {{ tm('detail.undo_signature') }}
              </Button>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Submit -->
      <Button
        :disabled="!canSubmit || isAcknowledging"
        class="w-full sm:w-auto"
        @click="handleSubmit"
      >
        <Loader2 v-if="isAcknowledging" class="mr-2 size-4 animate-spin" />
        {{ isAcknowledging ? tm('detail.acknowledging') : tm('detail.acknowledge_button') }}
      </Button>
    </div>
  </div>
</template>
